import React, { useEffect, useState } from "react";
import { X, Share, Plus, Download } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const KEY = "mytrack_a2hs_dismissed";
const ICON = "/mytrack-icon.png";

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isIOSDevice() {
  const ua = window.navigator.userAgent || "";
  return /iphone|ipad|ipod/i.test(ua) && !window.MSStream;
}

export function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [ios, setIos] = useState(false);
  const [deferred, setDeferred] = useState(null);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(KEY)) return;
    const iosDev = isIOSDevice();
    setIos(iosDev);

    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    let t;
    if (iosDev) t = setTimeout(() => setVisible(true), 2200);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      if (t) clearTimeout(t);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(KEY, "1");
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch (e) { /* ignore */ }
    dismiss();
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="fixed inset-x-0 bottom-0 z-[120] p-4"
          data-testid="install-prompt"
        >
          <div className="mx-auto flex max-w-md items-start gap-3 rounded-2xl border border-mt-border bg-mt-card p-4 shadow-2xl">
            <img src={ICON} className="h-14 w-14 shrink-0 rounded-xl" alt="MyTrackApp" />
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-head text-base font-bold">Add MyTrackApp to your phone</h3>
                <button onClick={dismiss} data-testid="install-dismiss" className="-mr-1 -mt-1 rounded-md p-1 text-mt-mutedfg hover:bg-mt-secondary hover:text-mt-text">
                  <X size={18} />
                </button>
              </div>
              {ios ? (
                <p className="mt-1 text-sm leading-relaxed text-mt-mutedfg">
                  Tap{" "}
                  <span className="inline-flex items-center gap-1 font-semibold text-mt-text"><Share size={13} /> Share</span>{" "}
                  then{" "}
                  <span className="inline-flex items-center gap-1 font-semibold text-mt-text">Add to Home Screen <Plus size={13} /></span>{" "}
                  for one-tap access next time.
                </p>
              ) : (
                <>
                  <p className="mt-1 text-sm leading-relaxed text-mt-mutedfg">
                    Install it for instant, full-screen access — right from your home screen.
                  </p>
                  <button onClick={install} data-testid="install-add" className="mt-3 inline-flex items-center gap-2 rounded-lg bg-mt-primary px-4 py-2 text-sm font-bold text-white hover:bg-mt-primary/90">
                    <Download size={16} /> Add to Home Screen
                  </button>
                </>
              )}
              <button onClick={dismiss} data-testid="install-already" className="mt-2 block text-xs font-semibold text-mt-mutedfg hover:underline">
                Already added? Dismiss
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
